import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import GoogleAnalytics from "./components/GoogleAnalytics";

// Core Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import { LegalCompliance } from "./pages/LegalCompliance";
import Success from "./pages/Success";

// Legal & Compliance Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Scam Check & Fraud Investigation
import ScamCheck from "./pages/ScamCheck";
import ScamCheckSubmit from "./pages/ScamCheckSubmit";
import ScamCheckUnlock from "./pages/ScamCheckUnlock";
import FraudInvestigation from "./pages/FraudInvestigation";
import ScamLibrary from "./pages/ScamLibrary";
import Investigations from "./pages/Investigations";
import CaseStudies from "./pages/CaseStudies";
import ReportScam from "./pages/ReportScam";
import InstantScamCheckPage from "./pages/InstantScamCheckPage";
import InstantResultsPage from "./pages/InstantResultsPage";
import ReportVerificationPage from "./pages/ReportVerificationPage";
import ScamCheckResultsPage from "./pages/ScamCheckResultsPage";
import VerifyReportPage from "./pages/VerifyReportPage";

// New Scam Intelligence Pages
import CheckResultPage from "./pages/CheckResultPage";
import ScamDatabasePage from "./pages/ScamDatabasePage";
import ScamCategoryPage from "./pages/ScamCategoryPage";

// Scalable SEO Entity Pages
import ScalableEntityPage from "./pages/ScalableEntityPage";

// Wrapper component for /is-*-legit routes that parses the entity from the URL
import { useParams, Navigate, useLocation } from "react-router-dom";

// Catch-all route component for handling /is-*-legit URL patterns
const CatchAllRoute = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // Check if it matches /is-*-legit pattern
  if (path.match(/^\/is-.+-legit$/)) {
    return <ScalableEntityPage />;
  }
  
  // For any other unmatched route, return null or a 404 page
  return null;
};

const IsLegitRouteWrapper = () => {
  // This catches /is-*-legit patterns and extracts the entity
  const { "*": wildcardPath } = useParams();
  // The wildcardPath will contain whatever comes after /is-
  // We need to handle this in ScalableEntityPage by detecting the URL pattern
  return <ScalableEntityPage />;
};

// Legacy/Entity Pages (keep for SEO)
import { ScamCheckPage } from "./pages/ScamCheckPage";
import { ScamDatabase } from "./pages/ScamDatabase";

// Admin
import { AdminDashboard } from "./pages/AdminDashboard";

function App() {
  return (
    <div className="App bg-[#0a1628] min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <GoogleAnalytics />
        <Header />
        <Routes>
          {/* Core Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/success" element={<Success />} />
          
          {/* Legal & Compliance */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/legal-compliance" element={<LegalCompliance />} />
          
          {/* Scam Check Flow */}
          <Route path="/scam-check" element={<ScamCheck />} />
          <Route path="/scam-check/submit" element={<ScamCheckSubmit />} />
          <Route path="/scam-check/unlock" element={<ScamCheckUnlock />} />
          <Route path="/scam-checker" element={<ScamCheckSubmit />} />
          <Route path="/check" element={<InstantScamCheckPage />} />
          <Route path="/instant-results" element={<InstantResultsPage />} />
          <Route path="/check/results" element={<InstantResultsPage />} />
          <Route path="/verify/:caseReference" element={<ReportVerificationPage />} />
          
          {/* New Post-Payment Results Flow */}
          <Route path="/scam-check-results" element={<ScamCheckResultsPage />} />
          <Route path="/verify-report" element={<VerifyReportPage />} />
          
          {/* Investigation Services */}
          <Route path="/investigations" element={<Investigations />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          
          {/* Fraud Intelligence - Dynamic Check Pages */}
          <Route path="/check/:type/:entity" element={<CheckResultPage />} />
          
          {/* Scam Database */}
          <Route path="/scam-database" element={<ScamDatabasePage />} />
          <Route path="/scams/:category" element={<ScamCategoryPage />} />
          
          {/* Fraud Investigation (legacy redirect) */}
          <Route path="/fraud-investigation" element={<Investigations />} />
          <Route path="/investigation-services" element={<Investigations />} />
          
          {/* Scam Library / Guides */}
          <Route path="/scam-library" element={<ScamLibrary />} />
          <Route path="/guides" element={<ScamLibrary />} />
          
          {/* Legacy Entity Pages (keep for SEO) */}
          <Route path="/scam-check/:slug" element={<ScamCheckPage />} />
          
          {/* SEO Routes - Scalable Entity Pages */}
          <Route path="/verify/:entity" element={<ScalableEntityPage />} />
          <Route path="/reviews/:entity" element={<ScalableEntityPage />} />
          
          {/* SEO Routes - /is-[entity]-legit pattern
              React Router v7 cannot handle prefix/suffix params in a single segment.
              Adding explicit routes for known entities + a fallback catch-all at the end */}
          
          {/* Domain Check SEO Route */}
          <Route path="/check/domain/:domain" element={<ScalableEntityPage />} />
          
          {/* Report a Scam */}
          <Route path="/report-a-scam" element={<ReportScam />} />
          <Route path="/report-scam" element={<ReportScam />} />
          
          {/* Admin */}
          <Route path="/admin" element={<AdminDashboard />} />
          
          {/* Catch-all for /is-*-legit pattern - must be at the end to not override other routes */}
          <Route path="*" element={<CatchAllRoute />} />
        </Routes>
        <Footer />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
